<template>
  <Modal
    ref="requestUpdateModalRef"
    :showIcon="false"
    :clickToClose="true"
    class="request-update-modal"
    :modalStyle="{
      width: '896px',
      height: '528px',
      padding: '24px',
    }"
    positiveActionText="Talep Oluştur"
    negativeActionText="Vazgeç"
    :isPositiveActionButtonDisabled="!isFormValid || isSubmitting"
    @onPositiveActionClick="handleSubmit"
    @onNegativeActionClick="handleClose"
    @modalClosed="handleModalClosed"
  >
    <template v-slot:body>
      <div class="header">
        <p class="title">Ürün Bilgileri için Güncelleme Talep Et/Hata Bildir</p>
      </div>

      <div class="scrollable-content">
        <div class="info-banner">
          <Icons name="InfoFilledIcon" size="md" color="#4FA4E5" />
          <span class="info-text">
            Önerdiğiniz ürün ismi, diğer mağazalar tarafından da kullanılabilir.
            Bu nedenle sadece ürüne ait bilgiler içermelidir.
          </span>
        </div>

        <div class="form-container">
          <div class="two-column-grid">
            <div class="column">
              <h3 class="column-title">Mevcut Bilgiler</h3>
              <div class="fields-container">
                <div
                  v-for="(field, index) in currentProductFeatures"
                  :key="`current-${index}`"
                  class="field-group"
                >
                  <Input
                    type="text"
                    :value="field.value"
                    :label="field.label"
                    :placeholder="field.label"
                    disabled
                    size="md"
                  />
                </div>
                <div class="category-change-note">
                  <div class="category-change-note-text">
                    Kategori değişimi talep edilemez.
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <h3 class="column-title">Talep Ettiğiniz Bilgiler</h3>

              <div class="fields-container">
                <div
                  v-for="(field, index) in requestedProductFeatures"
                  :key="`requested-${index}`"
                  class="field-group"
                >
                  <Input
                    type="text"
                    v-model:value="field.value"
                    :label="field.label"
                    size="md"
                  />
                </div>
                <div class="form-group description-group">
                  <div class="description-header">
                    <span class="description-title">Talep Açıklaması</span>
                    <span class="optional-badge">OPSİYONEL</span>
                  </div>

                  <Input
                    type="textarea"
                    v-model:value="description"
                    label="Talebinizi yazın"
                    :rows="4"
                    :maxlength="500"
                    size="md"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { Modal, Input, Icons } from '@n11/so-ui-components';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const store = useStore();
const requestUpdateModalRef = ref(null);
const sourceSection = ref(null);
const description = ref('');
const skipDismissOnClose = ref(false);
const isSubmitting = ref(false);

const productDetail = computed(
  () => store.getters['products/getProductDetail'],
);
const groupId = computed(() => productDetail.value?.groupId ?? null);

const currentProductFeatures = ref([
  {
    key: 'title',
    label: 'Ürün İsmi',
    value: '',
  },
  {
    key: 'category',
    label: 'Kategori',
    value: '',
  },
]);

const setFeatureValue = (key, value) => {
  const feature = currentProductFeatures.value.find((f) => f.key === key);
  if (feature) feature.value = value || '';
};

const requestedProductFeatures = reactive([
  { label: 'Önerdiğiniz ürün ismi', value: '' },
]);

const suggestedTitle = computed(() => 
  (requestedProductFeatures[0]?.value || '').trim(),
);
const feedbackDescription = computed(() => (description.value || '').trim());

const isFormValid = computed(() => {
  return (
    suggestedTitle.value.length > 0 || feedbackDescription.value.length > 0
  );
});

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  if (!groupId.value) {
    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description: 'groupId alınamadı. Lütfen sayfayı yenileyip tekrar deneyin.',
    });
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      groupId: groupId.value,
      title: suggestedTitle.value,
      description: feedbackDescription.value,
    };

    await store.dispatch('products/submitTitleFeedbackSuggestion', payload);
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_SUCCESS, {
      button_name: 'Talep Oluştur',
      button_type: 'Request Success',
      button_area: 'Product Information',
    });

    store.commit('common/SET_TOASTER_DATA', {
      type: 'success',
      title: 'Başarılı',
      description: 'Talep başarıyla oluşturuldu',
    });

    skipDismissOnClose.value = true;
    handleClose();
  } catch (error) {
    const errorMessage =
      error.errors && error.errors[0]
        ? error.errors[0].message
        : 'Bir hata oluştu. Lütfen tekrar deneyin.';

    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description: errorMessage,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  resetForm();
  requestUpdateModalRef.value?.hiddenModal();
};

const handleModalClosed = () => {
  if (!skipDismissOnClose.value) {
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Request Dismiss',
      button_area: 'Product Information',
    });
  }
  skipDismissOnClose.value = false;
};

const resetForm = () => {
  sourceSection.value = null;
  description.value = '';
  isSubmitting.value = false;

  requestedProductFeatures.forEach((field) => {
    field.value = '';
  });

  currentProductFeatures.value.forEach((f) => {
    f.value = '';
  });
};

const openModal = async (source) => {
  resetForm();
  sourceSection.value = source;

  setFeatureValue('title', productDetail.value?.title);
  setFeatureValue('category', productDetail.value?.categoryName);

  requestUpdateModalRef.value?.openModal();
};

defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.request-update-modal {
  :deep(.modal-container) {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  :deep(.modal-header) {
    position: absolute;
    top: 7px;
    right: 4px;
  }

  :deep(.modal-body) {
    padding: 0 !important;
    height: 100%;
    overflow: hidden !important;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden !important;
    }
  }

  :deep(.modal-footer) {
    padding: 8px 12px 0 0 !important;
  }
}

.header {
  margin-bottom: 12px;

  .title {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    text-align: left;
  }
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $colorN10;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: $colorN40;
    border-radius: 3px;

    &:hover {
      background: $colorN50;
    }
  }
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: $colorB05;
  border-radius: 6px;
  margin-bottom: 24px;

  .info-text {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: $colorN80;
  }
}

.two-column-grid {
  display: flex;
  gap: 48px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: $colorN20;
    transform: translateX(-50%);
  }

  .column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .column-title {
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
      margin-bottom: 4px;
      text-align: left;
    }

    .fields-container {
      display: flex;
      flex-direction: column;
      gap: 16px;

      :deep(.input-container) {
        margin-bottom: 0;
      }
    }

    &:nth-child(1) {
        .fields-container {
          padding-top:16px;
          gap: 0; 
        }

        .field-group {
          margin-bottom: 12px;
        }
        
        .field-group:nth-last-child(2) {
          margin-bottom: 4px;
        }
    }

    &:nth-child(2) .fields-container {
      background-color: #f5f3ef;
      border-radius: 8px;
      padding: 16px;
    }
  }
}

.form-group.description-group {
  margin-top: 0;

  :deep(.input-container) {
    margin-bottom: 0;
  }

  .description-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .description-title {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .optional-badge {
      font-size: 10px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      padding: 0 8px;
      color: $colorN60;
      background-color: $colorN00;
      border-radius: 3px;
      border: 0.5px solid $colorN30;
    }
  }
}
.category-change-note{
  margin-top: 0;
  display: flex;
  padding: 0 12px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  &-text{
    color: $colorN60;
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
  }
}
</style>
